// Admin API contracts (Phase 5, admin v2). Requests are zod-validated; responses are plain types
// shared by the API and apps/admin.
import { z } from 'zod';
import { Lang, type AdminRole, type JobStatus } from './enums.ts';

// ── Auth ──
export const AdminLoginBody = z.object({
  idToken: z.string().min(20),
  nonceToken: z.string().min(20),
});
export const AdminDevLoginBody = z.object({ telegramId: z.string().regex(/^\d+$/) });
export interface AdminLoginConfig {
  clientId: number;
  nonce: string;
  nonceToken: string;
  /** Local e2e only: the dev login endpoint is available. */
  devLogin?: boolean;
}
export interface AdminProfile {
  id: string;
  name: string;
  role: AdminRole;
  telegramId: string;
}
export interface AdminLoginResponse {
  token: string;
  expiresAt: string;
  admin: AdminProfile;
}

// ── Common ──
export const IdempotencyBody = z.object({ idempotencyKey: z.string().min(8).max(100) });
export interface Money {
  usd: number;
  uzs: number | null;
}
export interface FxInfo {
  rate: number;
  date: string;
  source: 'cbu' | 'manual';
}

// ── Readiness (home) ──
export type CheckState = 'ok' | 'missing' | 'error' | 'stub' | 'unchecked';
export type PermissionState = 'ok' | 'missing' | 'unknown';
export type StepStatus = 'done' | 'warn' | 'todo' | 'error' | 'locked';
export interface AdminReadinessStep {
  id: 'claude' | 'elevenlabs' | 'voices' | 'firstStory';
  status: StepStatus;
  detail: Record<string, unknown>;
}
export interface AdminReadiness {
  steps: AdminReadinessStep[];
  ready: boolean;
  paused: boolean;
  /** Paused by the CONTENT_PAUSED env (only Railway can lift it). */
  pausedByEnv: boolean;
  warnings: {
    id: 'tomorrowNoStory' | 'creditsLow' | 'keyBroken';
    detail: Record<string, unknown>;
  }[];
  credits: {
    monthUsed: number;
    monthLimit: number;
    provider: { used: number; limit: number; resetAt: string | null } | null;
  };
  checkedAt: string | null;
  /** Something is generating / voicing right now. */
  busy: AdminJob | null;
  inReview: number;
  scheduled: { id: string; title: string; publishAt: string }[];
}

export interface AdminEstimate {
  credits: number;
  creditsRemaining: number | null;
  monthLeft: number;
  claudeTokens: number;
  money: { tts: Money; claude: Money; total: Money };
  fx: FxInfo | null;
  enough: boolean;
}
export const AdminEstimateQuery = z.object({ storyId: z.uuid().optional(), lang: Lang.optional() });

// ── Stories ──
export const AdminStoryStatus = z.enum([
  'DRAFT',
  'IN_REVIEW',
  'SCHEDULED',
  'PUBLISHED',
  'ARCHIVED',
]);
export type AdminStoryStatus = z.infer<typeof AdminStoryStatus>;
export const AdminStoriesQuery = z.object({ status: AdminStoryStatus.optional() });
export type SourceType = 'AI' | 'FOLK' | 'AUTHOR';

export type ReviewFlag =
  | { kind: 'words'; lang: Lang; value: number; min: number; max: number }
  | { kind: 'score'; lang: Lang; value: number; min: number }
  | { kind: 'similar'; title: string; storyId: string };

export interface AdminCost {
  claudeInputTokens: number;
  claudeOutputTokens: number;
  ttsCredits: number;
  money: Money;
}

export interface AdminStoryListItem {
  id: string;
  slug: string;
  status: AdminStoryStatus;
  sourceType: SourceType;
  hue: number;
  flags: ReviewFlag[];
  qualityScore: number | null;
  theme: string | null;
  titles: Partial<Record<Lang, string>>;
  voiced: Lang[];
  publishAt: string | null;
  publishedAt: string | null;
  createdAt: string;
  cost: AdminCost;
  busy: boolean;
}
export interface AdminStoriesResponse {
  items: AdminStoryListItem[];
  counts: Record<AdminStoryStatus, number>;
}

export interface AdminVersion {
  lang: Lang;
  title: string;
  summary: string;
  text: string;
  words: number;
  qualityScore: number | null;
  /** Review checklist (one remark per item). */
  notes: string[];
  audioUrl: string | null;
  audioUrlLow: string | null;
  durationSec: number | null;
  audioOutdated: boolean;
  ttsModel: string | null;
  ttsCredits: number;
  /** Estimated credits to re-voice this language. */
  revoiceCredits: number;
  /** Text for the narrator with audio tags (null: the plain text is voiced). Never shown to users. */
  ttsScript: string | null;
  scriptWarnings: string[];
  textSource: 'MANUAL' | 'AI' | 'STT';
  /** "Текст вычитан ✓" — required for recognized uz text, a warning for ru/en. */
  textVerified: boolean;
  proofread: 'required' | 'recommended' | null;
  audioSource: 'TTS' | 'UPLOAD';
  upload: {
    mode: UploadMode;
    fileName: string | null;
    durationSec: number | null;
    /** The text changed after alignment: timings are rebuilt after "Текст вычитан ✓". */
    timingsStale: boolean;
    sttUsd: number | null;
  } | null;
  /** Word timings exist (read-along, Phase 5.2). */
  timedWords: number;
  /** Latest trial voicing of this language. */
  preview: {
    jobId: string;
    status: JobStatus;
    url: string | null;
    text: string | null;
    credits: number;
    createdAt: string;
  } | null;
  /** Credits of a trial voicing of the first paragraph (≈ characters). */
  previewCredits: number;
  /** Latest "Расставить автоматически": Claude's script, inserted into the editor on request. */
  suggestion: {
    jobId: string;
    status: JobStatus;
    script: string | null;
    tags: number;
    /** The text changed after the suggestion was made. */
    stale: boolean;
    createdAt: string;
  } | null;
}

export interface AdminJob {
  id: string;
  type: string;
  lang: Lang | null;
  status: JobStatus;
  attempts: number;
  errorCode: string | null;
  errorDetail: Record<string, unknown> | null;
  /** Raw provider / system text for "Подробнее". */
  error: string | null;
  retryLog: { at: string; attempt: number; code: string; message: string }[];
  cancelRequested: boolean;
  output: Record<string, unknown> | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  storyId: string | null;
  claudeInputTokens: number;
  claudeOutputTokens: number;
  ttsCredits: number;
  money: Money;
}

export interface AdminStoryDetail extends Omit<AdminStoryListItem, 'titles' | 'voiced'> {
  audioUploaded: boolean;
  /** Languages whose recognized text must be proofread before approval (uz). */
  unverified: Lang[];
  ageMin: number;
  ageMax: number;
  tags: string[];
  versions: AdminVersion[];
  jobs: AdminJob[];
}

const StoryTextInput = z.object({
  title: z.string().trim().min(2).max(120),
  summary: z.string().trim().min(10).max(400),
  text: z.string().trim().min(200).max(12_000),
});
export const AdminVersionPatch = StoryTextInput.partial().refine(
  (v) => Object.keys(v).length > 0,
  'empty patch',
);
export const AdminLangParams = z.object({ id: z.uuid(), lang: Lang });
export const AdminIdParams = z.object({ id: z.uuid() });
export const AdminApproveBody = z.object({ publishAt: z.iso.datetime().optional() });
export const AdminGenerateBody = IdempotencyBody.extend({
  theme: z.string().trim().max(300).optional(),
});
export const AdminCreateStoryBody = IdempotencyBody.extend({
  sourceType: z.enum(['FOLK', 'AUTHOR']),
  hue: z.int().min(0).max(359),
  ageMin: z.int().min(2).max(7),
  ageMax: z.int().min(2).max(7),
  tags: z.array(z.string().trim().min(1).max(40)).max(8),
  versions: z.object({ uz: StoryTextInput, ru: StoryTextInput, en: StoryTextInput }),
});

// ── Uploaded narration + audio tags (Phase 5.1) ──
export type UploadMode = 'audio' | 'audio_text' | 'text';
export const UploadKey = z
  .string()
  .regex(/^uploads\/[0-9a-f-]{36}\/(uz|ru|en)\.(mp3|wav|m4a)$/, 'unknown upload');
export const AdminUploadUrlBody = z.object({
  lang: Lang,
  fileName: z.string().trim().min(1).max(200),
  contentType: z.string().max(100),
  sizeBytes: z.int().positive(),
});
export interface AdminUploadUrlResponse {
  key: string;
  url: string;
  /** Headers the browser must send with the PUT (the signature covers them). */
  headers: Record<string, string>;
}
const UploadFile = z.object({
  uploadKey: UploadKey,
  fileName: z.string().trim().min(1).max(200),
  sizeBytes: z.int().positive(),
  durationSec: z
    .number()
    .positive()
    .max(6 * 3600)
    .optional(),
});
const Titles = StoryTextInput.pick({ title: true, summary: true });
export const AdminUploadVersion = z.discriminatedUnion('mode', [
  Titles.extend(UploadFile.shape).extend({ mode: z.literal('audio') }),
  StoryTextInput.extend(UploadFile.shape).extend({ mode: z.literal('audio_text') }),
  StoryTextInput.extend({ mode: z.literal('text') }),
]);
export type AdminUploadVersion = z.infer<typeof AdminUploadVersion>;
export const AdminUploadStoryBody = IdempotencyBody.extend({
  sourceType: z.enum(['AI', 'FOLK', 'AUTHOR']),
  hue: z.int().min(0).max(359),
  ageMin: z.int().min(2).max(7),
  ageMax: z.int().min(2).max(7),
  tags: z.array(z.string().trim().min(1).max(40)).max(8),
  versions: z.object({ uz: AdminUploadVersion, ru: AdminUploadVersion, en: AdminUploadVersion }),
});
export const AdminScriptBody = z.object({ script: z.string().max(20_000).nullable() });
export const AdminVerifyBody = z.object({ verified: z.boolean() });
export const AdminPreviewBody = IdempotencyBody.extend({
  paragraph: z.int().min(0).max(200).optional(),
  /** An unsaved fragment from the editor (tags allowed). */
  script: z.string().trim().min(1).max(2_000).optional(),
});
export const AdminDirectBody = IdempotencyBody;
/** Fresh audio without read-along timings (voiced before Phase 5.1). */
export interface AdminAlignMissing {
  versions: number;
  minutes: number;
}

export const AdminSimilarQuery = z.object({ title: z.string().trim().min(2).max(120) });
/** The 8 cover colours of the FOLK/AUTHOR form (hue). */
export const COVER_HUES = [12, 30, 48, 120, 170, 200, 250, 320] as const;

// ── Journal ──
export const AdminJournalQuery = z.object({
  filter: z.enum(['all', 'errors', 'active']).default('all'),
});
export interface AdminJournalGroup {
  /** Story id, or "auditions" / "other". */
  key: string;
  title: string;
  storyId: string | null;
  status: 'active' | 'failed' | 'done' | 'cancelled';
  cost: AdminCost;
  jobs: AdminJob[];
  lastAt: string;
}
export interface AdminJournalResponse {
  groups: AdminJournalGroup[];
  fx: FxInfo | null;
}

// ── Voices ──
export interface AdminVoiceSample {
  id: string;
  lang: Lang;
  voiceId: string;
  voiceName: string;
  modelId: string;
  chars: number;
  url: string;
  createdAt: string;
}
export interface AdminVoiceChoice {
  voiceId: string;
  voiceName: string;
  modelId: string;
}
export interface AdminVoicesResponse {
  selected: Record<Lang, AdminVoiceChoice | null>;
  samples: AdminVoiceSample[];
  lastJob: AdminJob | null;
  /** ≈ credits of a full audition (only uncached samples are voiced). */
  auditionCredits: number;
}
export const AdminAuditionBody = IdempotencyBody.extend({
  langs: z.array(Lang).min(1).max(3).optional(),
});
export const AdminSelectVoiceBody = z.object({ sampleId: z.uuid() });
export const AdminManualVoiceBody = z.object({
  lang: Lang,
  voiceId: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9]{10,40}$/, 'ElevenLabs voice id: 10–40 letters and digits'),
  modelId: z.enum(['eleven_v4', 'eleven_v3', 'eleven_multilingual_v2']),
  voiceName: z.string().trim().max(60).optional(),
});

// ── Settings ──
export interface AdminSettings {
  claudeModelGenerate: string;
  claudeModelReview: string;
  /** Models the Claude key can use (from the last check); fallback: the known list. */
  claudeModels: string[];
  ttsMonthlyCreditLimit: number;
  ttsMonthUsed: number;
  autoGenerateEnabled: boolean;
  generateTime: string;
  defaultPublishTime: string;
  reviewMinScore: number;
  broadcastTime: string;
  elevenLabsCreditUsd: number;
  fx: FxInfo | null;
  fxManual: number | null;
  voiceSettings: Record<Lang, VoiceSettingsValue>;
  autoTags: Record<Lang, boolean>;
}
export interface VoiceSettingsValue {
  stability: number;
  style: number;
}
const VoiceSettingsInput = z.object({
  stability: z.number().min(0).max(1),
  style: z.number().min(0).max(1),
});
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export const AdminSettingsPatch = z
  .object({
    claudeModelGenerate: z.string().regex(/^[a-z0-9][a-z0-9.-]{2,63}$/),
    claudeModelReview: z.string().regex(/^[a-z0-9][a-z0-9.-]{2,63}$/),
    ttsMonthlyCreditLimit: z.int().min(0).max(100_000_000),
    autoGenerateEnabled: z.boolean(),
    generateTime: hhmm,
    defaultPublishTime: hhmm,
    reviewMinScore: z.int().min(0).max(100),
    broadcastTime: hhmm,
    elevenLabsCreditUsd: z.number().positive().max(1),
    fxManual: z.number().positive().max(1_000_000).nullable(),
    voiceSettings: z
      .object({ uz: VoiceSettingsInput, ru: VoiceSettingsInput, en: VoiceSettingsInput })
      .partial(),
    autoTags: z.object({ uz: z.boolean(), ru: z.boolean(), en: z.boolean() }).partial(),
  })
  .partial();

export interface AdminUserItem {
  id: string;
  name: string;
  username: string | null;
  telegramId: string;
  role: AdminRole;
  active: boolean;
  receivesReports: boolean;
}
export const AdminUserPatch = z.object({ receivesReports: z.boolean() });
